import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { request } from "../../../../utils/Request";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik, FormikProps } from "formik";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { InputField } from "../../../../components/Shared/InputField";
import { isEmpty } from "lodash";

interface IProviderCredentialDrawerProps {
    visible: boolean;
    supplierId: string | null;
    providerSlug: string;
    onClose: () => void;
    toastify: any;
}

const defaultProviderLoginForm = {
    identification_number: null,
    email: null,
    password: null,
    secret: null,
};

const ProviderCredentialDrawer = (props: IProviderCredentialDrawerProps) => {
    const { visible, supplierId, providerSlug, onClose, toastify } = props;
    const { t } = useTranslation();
    const [initFormState, setInitFormState] = useState<any>(defaultProviderLoginForm);
    const [providerCredentialInfo, setProviderCredentialInfo] = useState<any>(null);

    const providerLoginValidationSchema = yup.object().shape({
        identification_number: yup.string().required(t('the field is required.', { field: t('identification number') })).nullable(),
        email: yup.string().email(t('the email must be a valid email address.')).required(t('the field is required.', { field: t('email') })).nullable(),
        password: yup.string().required(t('the field is required.', { field: t('password') })).nullable(),
        secret: yup.string().required(t('the field is required.', { field: t('secret') })).nullable(),
    });

    const providerLabel = (slug: string): string => {
        return slug === 'vehicle_logistics' ? t('vehicle logistics') : t('emil frey');
    };

    const longTextStyle = {
        whiteSpace: 'normal' as const,
        overflowWrap: 'anywhere' as const,
        wordBreak: 'break-word' as const,
    };

    const resetDrawerState = () => {
        setInitFormState(defaultProviderLoginForm);
        setProviderCredentialInfo(null);
    };

    const fetchProviderLoginPrefill = async (targetSupplierId: string, targetProviderSlug: string) => {
        try {
            const response = await request({
                url: `admin/suppliers/${ targetSupplierId }/providers/${ targetProviderSlug }`,
            });
            const { data } = response.data;
            setProviderCredentialInfo(data ?? null);
            setInitFormState({
                identification_number: data.identification_number ?? null,
                email: data.email ?? null,
                password: null,
                secret: data.secret ?? null,
            });
        } catch (e) {
            resetDrawerState();
        }
    };

    const onProviderLoginSubmit = async (payloads: any, { setErrors }: any) => {
        if ( isEmpty(supplierId) ) {
            return;
        }

        try {
            await request({
                method: 'POST',
                url: `admin/suppliers/${ supplierId }/providers/${ providerSlug }/token`,
                data: payloads,
            });

            toastify(t('login successful'), 'success');
            onClose();
        } catch (e: any) {
            if ( e.status === 422 ) {
                setErrors(e.data.errors);
                return;
            }

            toastify(t("server error"), "error");
        }
    };

    useEffect(() => {
        if ( visible && !isEmpty(supplierId) && !isEmpty(providerSlug) ) {
            fetchProviderLoginPrefill(supplierId as string, providerSlug);
        }
        if ( !visible ) {
            resetDrawerState();
        }
    }, [visible, supplierId, providerSlug]);

    return (
        <Sidebar
            header={
                <div className="bg-body-tertiary card-header">
                    <h5 className={ "card-title mt-0" }>{ providerLabel(providerSlug) } { t('connection') }</h5>
                    <p>{ t("using this form to login on alp") }</p>
                </div>
            }
            visible={ visible }
            position="right"
            onHide={ onClose }
            blockScroll={ true }
            style={ { width: '800px' } }
        >
            <Formik
                enableReinitialize={ true }
                initialValues={ initFormState }
                onSubmit={ onProviderLoginSubmit }
                validationSchema={ providerLoginValidationSchema }
            >
                { (providerLoginFormikProps: FormikProps<any>) => (
                    <Form onSubmit={ providerLoginFormikProps.handleSubmit }>
                        <Row className={ "mt-4" }>
                            <Col sm={ 12 } md={ 6 }>
                                <Field component={ InputField }
                                       name="identification_number"
                                       label={ t("identification number") }
                                       placeholder={ t("enter") + ' ' + t("identification number").toLowerCase() }
                                       isRequired />
                            </Col>
                            <Col sm={ 12 } md={ 6 }>
                                <Field component={ InputField }
                                       name="email"
                                       label={ t("email") }
                                       placeholder={ t("enter") + ' ' + t("email").toLowerCase() }
                                       isRequired />
                            </Col>
                            <Col sm={ 12 } md={ 6 }>
                                <Field component={ InputField }
                                       name="password"
                                       type={ "password" }
                                       label={ t("password") }
                                       placeholder={ t("enter") + ' ' + t("password").toLowerCase() }
                                       isRequired />
                            </Col>
                            <Col sm={ 12 } md={ 12 }>
                                <Field component={ InputField }
                                       name="secret"
                                       label={ t("secret") }
                                       placeholder={ t("enter") + ' ' + t("secret").toLowerCase() }
                                       isRequired />
                            </Col>
                        </Row>

                        <div className={ "mt-3" }>
                            <Button type={ "submit" } disabled={ providerLoginFormikProps.isSubmitting }
                                    size={ "small" }>
                                <i className="pi pi-save me-2" />
                                { t("let's login") }
                            </Button>
                            <Button type={ "button" } disabled={ providerLoginFormikProps.isSubmitting }
                                    onClick={ onClose } className={ "ms-2" } size={ "small" } severity={ "danger" }
                                    outlined>
                                <i className="pi pi-arrow-left me-2" />
                                { t("cancel") }
                            </Button>
                        </div>

                        {
                            providerCredentialInfo &&
                          <div className={ "mt-4 border rounded p-3 bg-light-subtle" }>
                              <h6 className={ "mb-3" }>{ t('provider credentials') }</h6>
                              <p className={ "mb-1" }>
                                  <strong>{ t('secret') }:</strong>
                                  <span style={ longTextStyle }> { providerCredentialInfo.secret || '-' }</span>
                              </p>
                              <p className={ "mb-1" }>
                                  <strong>{ t('token expires at') }:</strong> { providerCredentialInfo.token_expires_at || '-' }
                              </p>
                              <p className={ "mb-1" }>
                                  <strong>{ t('created at') }:</strong> { providerCredentialInfo.created_at || '-' }</p>
                              <p className={ "mb-1" }>
                                  <strong>{ t('provider supplier id') }:</strong> { providerCredentialInfo.provider_supplier_id || '-' }
                              </p>
                              <p className={ "mb-1" }>
                                  <strong>{ t('provider supplier name') }:</strong> { providerCredentialInfo.provider_supplier_name || '-' }
                              </p>
                              <p className={ "mb-0" }>
                                  <strong>{ t('provider supplier email') }:</strong> { providerCredentialInfo.provider_supplier_email || '-' }
                              </p>
                          </div>
                        }
                    </Form>
                ) }
            </Formik>
        </Sidebar>
    );
};

export default ProviderCredentialDrawer;
